import { useState, useEffect } from 'react';

const SecuritySettings = ({ settings, onSave, onChange }) => {
  const [formData, setFormData] = useState({
    requireStrongPasswords: settings?.requireStrongPasswords ?? true,
    passwordMinLength: settings?.passwordMinLength ?? 8,
    passwordRequireUppercase: settings?.passwordRequireUppercase ?? true,
    passwordRequireLowercase: settings?.passwordRequireLowercase ?? true,
    passwordRequireNumbers: settings?.passwordRequireNumbers ?? true,
    passwordRequireSymbols: settings?.passwordRequireSymbols ?? false,
    sessionTimeout: settings?.sessionTimeout ?? 60,
    maxLoginAttempts: settings?.maxLoginAttempts ?? 5,
    lockoutDuration: settings?.lockoutDuration ?? 15,
    twoFactorEnabled: settings?.twoFactorEnabled ?? false,
    backupEnabled: settings?.backupEnabled ?? true,
    backupFrequency: settings?.backupFrequency ?? 'daily',
    backupRetention: settings?.backupRetention ?? 30,
    backupLocation: settings?.backupLocation ?? 'local',
    auditLogging: settings?.auditLogging ?? true,
    logRetention: settings?.logRetention ?? 90,
    ipWhitelist: settings?.ipWhitelist ?? '',
    allowRemoteAccess: settings?.allowRemoteAccess ?? true
  });

  const [passwordTest, setPasswordTest] = useState('');

  useEffect(() => {
    if (settings) {
      setFormData({
        requireStrongPasswords: settings.requireStrongPasswords ?? true,
        passwordMinLength: settings.passwordMinLength ?? 8,
        passwordRequireUppercase: settings.passwordRequireUppercase ?? true,
        passwordRequireLowercase: settings.passwordRequireLowercase ?? true,
        passwordRequireNumbers: settings.passwordRequireNumbers ?? true,
        passwordRequireSymbols: settings.passwordRequireSymbols ?? false,
        sessionTimeout: settings.sessionTimeout ?? 60,
        maxLoginAttempts: settings.maxLoginAttempts ?? 5,
        lockoutDuration: settings.lockoutDuration ?? 15,
        twoFactorEnabled: settings.twoFactorEnabled ?? false,
        backupEnabled: settings.backupEnabled ?? true,
        backupFrequency: settings.backupFrequency ?? 'daily',
        backupRetention: settings.backupRetention ?? 30,
        backupLocation: settings.backupLocation ?? 'local',
        auditLogging: settings.auditLogging ?? true,
        logRetention: settings.logRetention ?? 90,
        ipWhitelist: settings.ipWhitelist ?? '',
        allowRemoteAccess: settings.allowRemoteAccess ?? true
      });
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : 
                     type === 'number' ? parseInt(value) || 0 : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    if (onChange) onChange();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const testPassword = (password) => {
    const tests = {
      length: password.length >= formData.passwordMinLength,
      uppercase: !formData.passwordRequireUppercase || /[A-Z]/.test(password),
      lowercase: !formData.passwordRequireLowercase || /[a-z]/.test(password),
      numbers: !formData.passwordRequireNumbers || /\d/.test(password),
      symbols: !formData.passwordRequireSymbols || /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    
    return tests;
  };

  const passwordTests = testPassword(passwordTest);
  const passwordValid = Object.values(passwordTests).every(test => test);

  const createBackup = () => {
    alert('Manual backup functionality will be implemented with backend integration');
  };

  const downloadLogs = () => {
    alert('Audit log download functionality will be implemented with backend integration');
  };

  return (
    <div className="card">
      <div className="card-header">
        <h6 className="mb-0">
          <i className="fas fa-shield-alt me-2"></i>
          Security Settings
        </h6>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* Password Policy */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Password Policy</h6>
            </div>
            
            <div className="col-12 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="requireStrongPasswords"
                  checked={formData.requireStrongPasswords}
                  onChange={handleChange}
                />
                <label className="form-check-label">
                  Enforce strong password requirements
                </label>
              </div>
            </div>
            
            {formData.requireStrongPasswords && (
              <>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Minimum Length</label>
                  <input
                    type="number"
                    className="form-control"
                    name="passwordMinLength"
                    value={formData.passwordMinLength}
                    onChange={handleChange}
                    min="6"
                    max="50"
                  />
                </div>
                
                <div className="col-md-9 mb-3">
                  <label className="form-label">Requirements</label>
                  <div className="row">
                    <div className="col-md-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="passwordRequireUppercase"
                          checked={formData.passwordRequireUppercase}
                          onChange={handleChange}
                        />
                        <label className="form-check-label">Uppercase</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="passwordRequireLowercase"
                          checked={formData.passwordRequireLowercase}
                          onChange={handleChange}
                        />
                        <label className="form-check-label">Lowercase</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="passwordRequireNumbers"
                          checked={formData.passwordRequireNumbers}
                          onChange={handleChange}
                        />
                        <label className="form-check-label">Numbers</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="passwordRequireSymbols"
                          checked={formData.passwordRequireSymbols}
                          onChange={handleChange}
                        />
                        <label className="form-check-label">Symbols</label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="col-12 mb-3">
                  <label className="form-label">Test Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={passwordTest}
                    onChange={(e) => setPasswordTest(e.target.value)}
                    placeholder="Test your password requirements here"
                  />
                  {passwordTest && (
                    <div className="mt-2">
                      <small className={`badge ${passwordValid ? 'bg-success' : 'bg-danger'} me-2`}>
                        {passwordValid ? 'Valid' : 'Invalid'}
                      </small>
                      <small className={`me-2 ${passwordTests.length ? 'text-success' : 'text-danger'}`}>
                        Length ({formData.passwordMinLength}+)
                      </small>
                      {formData.passwordRequireUppercase && (
                        <small className={`me-2 ${passwordTests.uppercase ? 'text-success' : 'text-danger'}`}>
                          Uppercase
                        </small>
                      )}
                      {formData.passwordRequireLowercase && (
                        <small className={`me-2 ${passwordTests.lowercase ? 'text-success' : 'text-danger'}`}>
                          Lowercase
                        </small>
                      )}
                      {formData.passwordRequireNumbers && (
                        <small className={`me-2 ${passwordTests.numbers ? 'text-success' : 'text-danger'}`}>
                          Numbers
                        </small>
                      )}
                      {formData.passwordRequireSymbols && (
                        <small className={`me-2 ${passwordTests.symbols ? 'text-success' : 'text-danger'}`}>
                          Symbols
                        </small>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Session Security */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Session Security</h6>
            </div>
            
            <div className="col-md-4 mb-3">
              <label className="form-label">Session Timeout (minutes)</label>
              <input
                type="number"
                className="form-control"
                name="sessionTimeout"
                value={formData.sessionTimeout}
                onChange={handleChange}
                min="5"
                max="480"
              />
            </div>
            
            <div className="col-md-4 mb-3">
              <label className="form-label">Max Login Attempts</label>
              <input
                type="number"
                className="form-control"
                name="maxLoginAttempts"
                value={formData.maxLoginAttempts}
                onChange={handleChange}
                min="3"
                max="10"
              />
            </div>
            
            <div className="col-md-4 mb-3">
              <label className="form-label">Lockout Duration (minutes)</label>
              <input
                type="number"
                className="form-control"
                name="lockoutDuration"
                value={formData.lockoutDuration}
                onChange={handleChange}
                min="5"
                max="1440"
              />
            </div>
            
            <div className="col-12 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="twoFactorEnabled"
                  checked={formData.twoFactorEnabled}
                  onChange={handleChange}
                />
                <label className="form-check-label">
                  Enable Two-Factor Authentication (2FA)
                </label>
              </div>
            </div>
          </div>

          {/* Access Control */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Access Control</h6>
            </div>
            
            <div className="col-12 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="allowRemoteAccess"
                  checked={formData.allowRemoteAccess}
                  onChange={handleChange}
                />
                <label className="form-check-label">
                  Allow remote access
                </label>
              </div>
            </div>
            
            <div className="col-12 mb-3">
              <label className="form-label">IP Whitelist (one per line)</label>
              <textarea
                className="form-control"
                name="ipWhitelist"
                value={formData.ipWhitelist}
                onChange={handleChange}
                rows="4"
                placeholder="192.168.1.0/24&#10;10.0.0.0/8&#10;172.16.0.0/12"
              />
              <div className="form-text">Leave empty to allow all IPs. Use CIDR notation for ranges.</div>
            </div>
          </div>

          {/* Backup Settings */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Backup Settings</h6>
            </div>
            
            <div className="col-12 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="backupEnabled"
                  checked={formData.backupEnabled}
                  onChange={handleChange}
                />
                <label className="form-check-label">
                  Enable automatic backups
                </label>
              </div>
            </div>
            
            {formData.backupEnabled && (
              <>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Backup Frequency</label>
                  <select
                    className="form-select"
                    name="backupFrequency"
                    value={formData.backupFrequency}
                    onChange={handleChange}
                  >
                    <option value="hourly">Every Hour</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                
                <div className="col-md-4 mb-3">
                  <label className="form-label">Retention Period (days)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="backupRetention"
                    value={formData.backupRetention}
                    onChange={handleChange}
                    min="1"
                    max="365"
                  />
                </div>
                
                <div className="col-md-4 mb-3">
                  <label className="form-label">Backup Location</label>
                  <select
                    className="form-select"
                    name="backupLocation"
                    value={formData.backupLocation}
                    onChange={handleChange}
                  >
                    <option value="local">Local Server</option>
                    <option value="cloud">Cloud Storage</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                
                <div className="col-12">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={createBackup}
                  >
                    <i className="fas fa-download me-1"></i>
                    Create Manual Backup
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Audit Logging */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Audit Logging</h6>
            </div>
            
            <div className="col-12 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="auditLogging"
                  checked={formData.auditLogging}
                  onChange={handleChange}
                />
                <label className="form-check-label">
                  Enable audit logging
                </label>
              </div>
            </div>
            
            {formData.auditLogging && (
              <>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Log Retention (days)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="logRetention"
                    value={formData.logRetention}
                    onChange={handleChange}
                    min="30"
                    max="365"
                  />
                </div>
                
                <div className="col-12">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={downloadLogs}
                  >
                    <i className="fas fa-download me-1"></i>
                    Download Audit Logs
                  </button>
                </div>
              </>
            )}
          </div>

          <button type="submit" className="btn btn-primary">
            <i className="fas fa-save me-1"></i>
            Save Security Settings
          </button>
        </form>
      </div>
    </div>
  );
};

export default SecuritySettings;